import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../../../UserContext';
import { adminService } from '../../../../services';
import { ArrowLeft, AlertTriangle, ShoppingBag, Search, Trash2 } from 'lucide-react';
import './Admin.css';

const AdminMarketplace = () => {
    const { user } = useUser();
    const [activeTab, setActiveTab] = useState('listings');
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState('All');
    const [listings, setListings] = useState([]);
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError('');
            const params = new URLSearchParams({
                page: String(page),
                pageSize: '20',
                status
            });
            if (query.trim()) {
                params.append('query', query.trim());
            }

            if (activeTab === 'listings') {
                const data = await adminService.getMarketplaceListings(params);
                setListings(data.listings || []);
                setTotalPages(data.totalPages || 1);
                setTotalCount(data.totalCount || 0);
            } else {
                const data = await adminService.getMarketplaceOffers(params);
                setOffers(data.offers || []);
                setTotalPages(data.totalPages || 1);
                setTotalCount(data.totalCount || 0);
            }
        } catch (err) {
            setError(err.message || 'Failed to load marketplace admin data.');
        } finally {
            setIsLoading(false);
        }
    }, [activeTab, page, query, status]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleListingStatusChange = async (listingId, nextStatus) => {
        try {
            await adminService.updateMarketplaceListingStatus(listingId, { status: nextStatus });
            await fetchData();
        } catch (err) {
            setError(err.message || 'Failed to update listing status.');
        }
    };

    const handleOfferStatusChange = async (offerId, nextStatus) => {
        try {
            await adminService.updateMarketplaceOfferStatus(offerId, { status: nextStatus });
            await fetchData();
        } catch (err) {
            setError(err.message || 'Failed to update offer status.');
        }
    };

    const handleDeleteListing = async (listingId, title) => {
        if (!window.confirm(`Delete listing "${title}"? This cannot be undone.`)) {
            return;
        }

        try {
            await adminService.deleteMarketplaceListing(listingId);
            await fetchData();
        } catch (err) {
            setError(err.message || 'Failed to delete listing.');
        }
    };

    if (!user?.isAdmin) {
        return (
            <div className="admin-page">
                <div className="admin-access-denied">
                    <AlertTriangle size={48} />
                    <h2>Access Denied</h2>
                    <p>You do not have permission to access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div className="admin-header-content">
                    <Link to="/dashboard/admin" className="admin-back-btn">
                        <ArrowLeft size={20} />
                    </Link>
                    <div className="admin-header-icon">
                        <ShoppingBag size={28} />
                    </div>
                    <div>
                        <h1>Marketplace Moderation</h1>
                        <p>{totalCount} {activeTab} in view</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="admin-alert admin-alert-error">
                    <AlertTriangle size={20} />
                    {error}
                </div>
            )}

            <div className="admin-users-controls">
                <div className="admin-equipment-tabs" style={{ marginBottom: 12 }}>
                    <button className={`admin-btn ${activeTab === 'listings' ? 'admin-btn-primary' : 'admin-btn-secondary'}`} onClick={() => { setActiveTab('listings'); setPage(1); }}>
                        Listings
                    </button>
                    <button className={`admin-btn ${activeTab === 'offers' ? 'admin-btn-primary' : 'admin-btn-secondary'}`} onClick={() => { setActiveTab('offers'); setPage(1); }}>
                        Offers
                    </button>
                </div>

                <form
                    className="admin-search-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setPage(1);
                        fetchData();
                    }}
                >
                    <div className="admin-search-input">
                        <Search size={20} />
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${activeTab}...`} />
                    </div>
                    <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                        <option value="All">All Statuses</option>
                        {activeTab === 'listings' ? (
                            <>
                                <option value="Active">Active</option>
                                <option value="Draft">Draft</option>
                                <option value="Sold">Sold</option>
                                <option value="Expired">Expired</option>
                                <option value="Removed">Removed</option>
                            </>
                        ) : (
                            <>
                                <option value="Pending">Pending</option>
                                <option value="Accepted">Accepted</option>
                                <option value="Rejected">Rejected</option>
                                <option value="Withdrawn">Withdrawn</option>
                                <option value="Expired">Expired</option>
                            </>
                        )}
                    </select>
                    <button type="submit" className="admin-btn admin-btn-primary">Search</button>
                </form>
            </div>

            {isLoading ? (
                <div className="admin-loading">
                    <div className="admin-spinner" />
                    <p>Loading moderation data...</p>
                </div>
            ) : activeTab === 'listings' ? (
                <div className="admin-users-table-container">
                    <table className="admin-users-table">
                        <thead>
                            <tr>
                                <th>Listing</th>
                                <th>Seller</th>
                                <th>Status</th>
                                <th>Price</th>
                                <th>Offers</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listings.map((listing) => (
                                <tr key={listing.listingId}>
                                    <td>
                                        <strong>{listing.title}</strong>
                                        <div className="admin-user-email">{listing.category}</div>
                                    </td>
                                    <td>{listing.sellerName} ({listing.sellerType})</td>
                                    <td>{listing.status}</td>
                                    <td>GBP {Number(listing.price || 0).toLocaleString('en-GB')}</td>
                                    <td>{listing.offerCount}</td>
                                    <td>
                                        <div className="admin-user-actions">
                                            <select value={listing.status} onChange={(e) => handleListingStatusChange(listing.listingId, e.target.value)}>
                                                <option value="Draft">Draft</option>
                                                <option value="Active">Active</option>
                                                <option value="Sold">Sold</option>
                                                <option value="Expired">Expired</option>
                                                <option value="Removed">Removed</option>
                                            </select>
                                            <button className="admin-btn admin-btn-danger" onClick={() => handleDeleteListing(listing.listingId, listing.title)}>
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="admin-users-table-container">
                    <table className="admin-users-table">
                        <thead>
                            <tr>
                                <th>Offer</th>
                                <th>Listing</th>
                                <th>Buyer</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {offers.map((offer) => (
                                <tr key={offer.offerId}>
                                    <td>GBP {Number(offer.offerAmount || 0).toLocaleString('en-GB')}</td>
                                    <td>{offer.listingTitle}</td>
                                    <td>{offer.buyerName}</td>
                                    <td>{offer.status}</td>
                                    <td>
                                        <select value={offer.status} onChange={(e) => handleOfferStatusChange(offer.offerId, e.target.value)}>
                                            <option value="Pending">Pending</option>
                                            <option value="Accepted">Accepted</option>
                                            <option value="Rejected">Rejected</option>
                                            <option value="Withdrawn">Withdrawn</option>
                                            <option value="Expired">Expired</option>
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="admin-pagination">
                <button className="admin-btn admin-btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                </button>
                <div className="admin-pagination-info">Page {page} of {Math.max(1, totalPages)}</div>
                <button className="admin-btn admin-btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                    Next
                </button>
            </div>
        </div>
    );
};

export default AdminMarketplace;
